// Copyright 2023 Sorama B.V.

import { DialogService } from "aurelia-dialog";
import { autoinject, BindingEngine, computedFrom, Disposable, PLATFORM, transient } from "aurelia-framework";
import { WorkflowModalCatalog } from "catalogs/WorkflowModalCatalog";
import { MeasurementWorkflowModuleCatalog } from "catalogs/WorkflowModuleCatalog/MeasurementModuleCatalog";
import { Filter, Sorter } from "controls/filter-menu/FilterMenu";
import { ProjectDTO } from "datatypes/SoramaWebApi";
import {
    IWorkflowModuleInstance,
    IWorkflowModuleLifetime,
} from "libraries/WorkflowFramework/Interfaces/IWorkflowModule";
import { WorkflowPropertyBindingMode } from "libraries/WorkflowFramework/Interfaces/IWorkflowPropertyMetadata";
import { workflowProperty } from "libraries/WorkflowFramework/WorkflowProperties/WorkflowPropertyAttribute";
import { WorkflowUriModule } from "libraries/WorkflowFramework/WorkflowProperties/WorkflowPropertyUriConverter";
import { BatchOperationResultModel } from "modules/dialogs/BatchOperationResult/BatchOperationResultDialog";
import { ConfirmationModel } from "modules/dialogs/Confirmation/ConfirmationDialog";
import { ProjectSelectionModel } from "modules/dialogs/ProjectSelection/ProjectSelectionDialog";
import { MeasurementCollectionService } from "services/Measurement/MeasurementCollectionService";
import { Measurement } from "services/Measurement/MeasurementTypes";
import { IProjectManagementService } from "services/Project/IProjectManagementService";

export type SortingOption = "DateAscending" | "DateDescending" | "NameAscending" | "NameDescending";

@transient()
@autoinject
export class MeasurementsListModule implements IWorkflowModuleLifetime {
    //#region View Properties
    private isMeasurementSelected: { [id: string]: boolean } = {};
    private filteredMeasurements: Measurement[] = [];
    //#endregion

    private disposables: Disposable[];

    constructor(
        private readonly bindingEngine: BindingEngine,

        private readonly measurementCollectionService: MeasurementCollectionService,
        private readonly projectManagementService: IProjectManagementService,
        private readonly dialogService: DialogService
    ) {}

    private moduleInstance: IWorkflowModuleInstance;

    private get selectedMeasurementIds(): string[] {
        return Object.keys(this.isMeasurementSelected).filter((id) => this.isMeasurementSelected[id]);
    }

    @computedFrom("allMeasurements", "selectedMeasurementIds")
    private get selectedMeasurements(): Measurement[] {
        return this.allMeasurements.filter((m) => this.selectedMeasurementIds.includes(m.id));
    }

    @computedFrom("selectedMeasurementIds")
    private get anySelected(): boolean {
        return this.selectedMeasurementIds.length > 0;
    }

    @computedFrom("measurementCollectionService.measurements", "showAllLocalMeasurements")
    private get allMeasurements(): Measurement[] {
        let allMeasurements = this.measurementCollectionService.measurements.filter((m) => m.canAnalyse);
        if (this.showAllLocalMeasurements) {
            allMeasurements = allMeasurements.filter((m) => m.origin.has("Local"));
        } else {
            allMeasurements = allMeasurements.filter((m) => m.origin.has("Cloud"));
        }
        return allMeasurements;
    }

    @computedFrom("selectedMeasurementIds")
    private get isSomeMeasurementSelected(): boolean {
        return this.selectedMeasurementIds.length > 0;
    }
    @computedFrom("selectedMeasurementIds", "isMeasurementSelected")
    private get isEveryMeasurementSelected(): boolean {
        if (this.selectedMeasurementIds.length === 0) return false;
        return Object.values(this.isMeasurementSelected).every((x) => x);
    }

    @computedFrom("selectedMeasurementId")
    private get selectedMeasurement(): Measurement {
        return this.allMeasurements.find((m) => m.id === this.selectedMeasurementId);
    }

    @computedFrom(
        "allMeasurements",
        "showAllLocalMeasurements",
        "measurementCollectionService.measurements",
        "measurementCollectionService.isLoading"
    )
    private get isEmptyProject(): boolean {
        if (this.allMeasurements.length === 0 && !this.measurementCollectionService.isLoading) return true;
        const projectMeasurements = this.allMeasurements.filter((m) => m.origin.has("Cloud"));
        return (
            projectMeasurements.length === 0 &&
            !this.showAllLocalMeasurements &&
            !this.measurementCollectionService.isLoading
        );
    }

    private get isLoadingProject(): boolean {
        return this.projectManagementService.currentProject.status.inProgress;
    }

    //#region Workflow Properties
    @workflowProperty({ bindingMode: WorkflowPropertyBindingMode.twoWay })
    private selectedMeasurementId: string;

    @workflowProperty({ bindingMode: WorkflowPropertyBindingMode.twoWay })
    private selectedGroupId: string;

    @workflowProperty({ bindingMode: WorkflowPropertyBindingMode.twoWay })
    private showAllLocalMeasurements = false;

    //#endregion

    //#region Filter and Sorter
    private measurementFilters: Filter<Measurement>[] = this.showAllLocalMeasurements
        ? []
        : [
              { name: "Local", filter: (m: Measurement) => m.dataLocation.has("Local") },
              { name: "Cloud", filter: (m: Measurement) => m.dataLocation.has("Cloud") },
          ];

    private measurementSorters: Sorter<Measurement>[] = [
        {
            name: "Name",
            icon: "fas fa-sort-alpha",
            comparator: (m1: Measurement, m2: Measurement) => m1.name?.localeCompare(m2.name),
        },
        {
            name: "Date",
            icon: "fas fa-sort-numeric",
            comparator: (m1: Measurement, m2: Measurement) =>
                new Date(m1.created).getTime() - new Date(m2.created).getTime(),
        },
    ];
    //#endregion

    public bind(): void {
        this.disposables = [
            this.bindingEngine
                .propertyObserver(this.measurementCollectionService, "measurements")
                .subscribe(() => this.measurementsChanged()),
        ];
    }

    public activateModule(moduleInstance: IWorkflowModuleInstance): void | Promise<void> {
        this.moduleInstance = moduleInstance;
        this.selectedMeasurementId = null;
        this.closeMeasurementInfo();
        this.measurementCollectionService.refreshAllMeasurements();
    }

    public deactivateModule(): void | Promise<void> {}

    public unbind(): void {
        while (this.disposables.length > 0) {
            this.disposables.pop().dispose();
        }
    }

    public async measurementsChanged(): Promise<void> {
        this.clearSelection();
        this.selectedMeasurementId = undefined;
        this.closeMeasurementInfo();
    }

    //#region Measurement property helpers

    //#endregion

    //#region Click event handlers
    public toggleSelectMeasurement(measurement: Measurement, event: Event) {
        this.isMeasurementSelected[measurement.id] = !this.isMeasurementSelected[measurement.id];
        event.stopPropagation();
    }

    public toggleAllSelected(e: Event): void {
        // For some reason prevent default is not preventing the checked state from being changed,
        // So our default behaviour is executed after this function finishes, making the checkbox toggle to the wrong value
        // so we need to use setTimeout to trigger a reevaluation of the computed property slightly later, so that the checkbox's checked state always corresponds to the computed property
        e.preventDefault();
        setTimeout(() => {
            if (this.isSomeMeasurementSelected) {
                this.isMeasurementSelected = {};
            } else {
                this.filteredMeasurements.forEach((m) => (this.isMeasurementSelected[m.id] = true));
            }
        }, 0);
    }

    public async closeMeasurementInfo(): Promise<void> {
        const recordingModules = this.moduleInstance
            .getWorkflow()
            .getModules(MeasurementWorkflowModuleCatalog.MeasurementModule);
        for (const module of recordingModules) module.close();
    }

    public showMeasurementInfo(measurement: Measurement, event?: Event): void {
        if (this.selectedMeasurementId === measurement.id) {
            this.closeMeasurementInfo();
            this.selectedMeasurementId = undefined;
            return;
        }

        if (!this.selectedMeasurementId) {
            this.selectedMeasurementId = measurement.id;
            const newMeasurementModule = MeasurementWorkflowModuleCatalog.MeasurementModule as WorkflowUriModule;
            const currentPosition = this.moduleInstance.getWorkflow().getModulePosition(this.moduleInstance.moduleId);
            newMeasurementModule.position = currentPosition + 1;
            this.moduleInstance.getWorkflow().addModule(newMeasurementModule);
            return;
        }

        this.selectedMeasurementId = measurement.id;

        event.stopPropagation();
    }

    private async uploadMeasurementsClicked(): Promise<void> {
        const failedMeasurements = await this.measurementCollectionService.batchOperation(
            this.selectedMeasurementIds,
            (m) => m.upload(this.projectManagementService.currentProject.item.id)
        );

        if (failedMeasurements.length > 0) {
            const batchOperationResultModel: BatchOperationResultModel = {
                title: "Sorry!",
                text: "Certain measurements could not be uploaded:",
                results: failedMeasurements.map((f) => {
                    return {
                        name: f.measurement.name,
                        message: f.reason,
                    };
                }),
            };
            this.dialogService.open({
                viewModel: PLATFORM.moduleName("modules/dialogs/BatchOperationResult/BatchOperationResultDialog"),
                model: batchOperationResultModel,
                lock: true,
            });
        }

        this.refresh();
    }

    public deleteMeasurementsClicked(): void {
        if (!this.anySelected) return;
        const pluralString = this.selectedMeasurementIds.length > 1 ? "s" : "";
        const confirmationModel: ConfirmationModel = {
            title: `Delete Measurement${pluralString}`,
            text: `Are you sure you want to delete the selected measurement${pluralString}? This action cannot be undone.`,
            confirmText: "Delete",
            operation: async () => {
                const refreshNeeded = await this.deleteMeasurements();
                if (refreshNeeded) this.refresh();
            },
        };

        this.dialogService.open({
            viewModel: PLATFORM.moduleName("modules/dialogs/Confirmation/ConfirmationDialog"),
            model: confirmationModel,
            lock: true,
        });
    }

    private async refresh(): Promise<void> {
        this.clearSelection();
        await this.measurementCollectionService.refreshAllMeasurements();
    }

    private async deleteMeasurements(): Promise<boolean> {
        if (!this.canDeleteSelection) return false;
        const failedMeasurements = await this.measurementCollectionService.batchOperation(
            this.selectedMeasurementIds,
            (m) => m.delete(this.projectManagementService.currentProject.item.id)
        );

        // Ensure refresh completes before proceeding
        await this.measurementCollectionService.refreshAllMeasurements();

        if (failedMeasurements.length === 0) {
            this.clearSelection();
            return true;
        }

        const batchOperationResultModel: BatchOperationResultModel = {
            title: "Sorry!",
            text: "Certain measurements could not be deleted:",
            results: failedMeasurements.map((f) => {
                return {
                    name: f.measurement.name,
                    message: f.reason,
                };
            }),
        };
        await this.dialogService.open({
            viewModel: PLATFORM.moduleName("modules/dialogs/BatchOperationResult/BatchOperationResultDialog"),
            model: batchOperationResultModel,
            lock: true,
        });
    }

    public async exportMeasurementsClicked(): Promise<void> {
        if (!this.canExportSelection) return;

        const failedMeasurements = await this.measurementCollectionService.batchExport(this.selectedMeasurementIds);

        if (failedMeasurements.length === 0) return;

        const batchOperationResultModel: BatchOperationResultModel = {
            title: "Sorry!",
            text: "Certain measurements could not be exported:",
            results: failedMeasurements.map((f) => {
                return {
                    name: f.measurement.name,
                    message: f.reason,
                };
            }),
        };
        await this.dialogService.open({
            viewModel: PLATFORM.moduleName("modules/dialogs/BatchOperationResult/BatchOperationResultDialog"),
            model: batchOperationResultModel,
            lock: true,
        });

        this.clearSelection();
    }

    public async importExternalFilesClicked(): Promise<void> {
        await this.moduleInstance.createModal(WorkflowModalCatalog.FileImporter).show();
    }

    public async transferMeasurementsClicked(): Promise<void> {
        if (!this.canTransferSelection) return;
        const measurementTransferModel: ProjectSelectionModel = {
            title: "Transfer Measurements",
            text: `Transferring the selected measurements from '${this.projectManagementService.currentProject.item.name}' to:`,
            selectText: "Transfer",
            filter: (project: ProjectDTO) => project.id !== this.projectManagementService.currentProject.item.id,
        };

        await this.dialogService
            .open({
                viewModel: PLATFORM.moduleName("modules/dialogs/ProjectSelection/ProjectSelectionDialog"),
                model: measurementTransferModel,
                lock: true,
            })
            .whenClosed(async (result): Promise<boolean> => {
                if (result.wasCancelled) return false;

                const projectToTransferFromId = this.showAllLocalMeasurements
                    ? null
                    : this.projectManagementService.currentProject.item.id;
                const projectToTransferTo = result.output;

                let selectedMeasurementNames = this.selectedMeasurements
                    .filter((m) => m.name != undefined)
                    .map((m) => m.name.trim());

                // Filters out duplicate names in the selected measurements and checks if they exist in the target project
                let duplicateMeasurementNames = selectedMeasurementNames.filter((fileName) =>
                    this.allMeasurements.some(
                        (m) => m.projectId == projectToTransferTo.id && m.name?.trim() === fileName
                    )
                );

                let failedMeasurements = [];

                // If there are duplicate names, show error and do not continue transfer
                if (duplicateMeasurementNames.length === 0) {
                    failedMeasurements = await this.measurementCollectionService.batchOperation(
                        this.selectedMeasurementIds,
                        (x: Measurement) => x.transfer(projectToTransferFromId, projectToTransferTo.id)
                    );
                }

                let batchResultModel: BatchOperationResultModel;
                if (duplicateMeasurementNames.length > 0) {
                    batchResultModel = {
                        title: "Transfer Failed",
                        text: "Some measurements could not be transferred due to duplicate names:",
                        closeText: "View Project",
                        cancelText: "Ok",
                        results: duplicateMeasurementNames.map((name) => {
                            return {
                                name: name,
                                message: `A measurement with the name "${name}" has already been imported.`,
                            };
                        }),
                    };
                } else if (failedMeasurements.length === 0) {
                    // Show success message
                    batchResultModel = {
                        title: "Success!",
                        text: `Your measurements were added to project: '${projectToTransferTo.name}'`,
                        closeText: "View Project",
                        cancelText: "Ok",
                    };
                } else {
                    batchResultModel = {
                        title: "Sorry!",
                        text: "Certain measurements could not be transferred:",
                        closeText: "View Project",
                        cancelText: "Ok",
                        results: failedMeasurements.map((f) => {
                            return {
                                name: f.measurement.name,
                                message: f.reason,
                            };
                        }),
                    };
                }

                await this.dialogService
                    .open({
                        viewModel: PLATFORM.moduleName(
                            "modules/dialogs/BatchOperationResult/BatchOperationResultDialog"
                        ),
                        model: batchResultModel,
                        lock: true,
                    })
                    .whenClosed((result) => {
                        if (result.wasCancelled) {
                            this.refresh();
                        } else {
                            this.projectManagementService.setCurrentProject(projectToTransferTo.id);
                        }
                        this.showAllLocalMeasurements = false;
                    });
            });
    }

    //#endregion

    private clearSelection() {
        this.isMeasurementSelected = {};
    }

    private get canDeleteSelection(): boolean {
        return this.anySelected && this.selectedMeasurements.some((m) => m.allowedOperations.has("delete"));
    }

    private get canUploadSelection(): boolean {
        return this.anySelected && this.selectedMeasurements.some((m) => m.allowedOperations.has("upload"));
    }

    private get canExportSelection(): boolean {
        return this.anySelected && this.selectedMeasurements.some((m) => m.allowedOperations.has("export"));
    }

    private get canTransferSelection(): boolean {
        return this.anySelected && this.selectedMeasurements.some((m) => m.allowedOperations.has("transfer"));
    }
}
